/**
 * Expert panel authentication (US-01-005).
 *
 * THERE IS NO LOOKUP HOOK AND NO REGISTRATION HOOK, and both absences are
 * requirements: the panel must not reveal whether a number belongs to an Expert
 * (US-01-005.6/.7), and it exposes no self-sign-up route at all (US-01-005.13).
 *
 * The session JWT is carried ONLY in an httpOnly, Secure, SameSite=Lax cookie —
 * the panel is web-only, so unlike the user app there is no token in the response
 * body and nothing for this app to store (US-01-005.12).
 */

import { analytics, track } from "@anthra/platform/analytics"
import {
	fetchPanelSession,
	panelSignOut,
	requestPanelOtp,
	verifyPanelOtp,
} from "@anthra/platform/api"
import { isOtpLockout, lockoutMessage } from "@anthra/platform/errors"
import { logger } from "@anthra/platform/logger"
import { endSession, takeDestination } from "@anthra/platform/session"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { usePanelEntryStore } from "@/stores/use-entry-store"
import { useSessionStore } from "@/stores/use-session-store"

export const panelAuthKeys = {
	all: ["panel-auth"] as const,
	session: () => [...panelAuthKeys.all, "session"] as const,
}

/**
 * The current panel session.
 *
 * Re-read rather than trusted: `isActive` is checked on every authenticated
 * request, so a Super Admin's deactivation lands on the Expert's very next call
 * rather than at their next login (US-01-005.17).
 */
export function usePanelSession() {
	return useQuery({
		queryKey: panelAuthKeys.session(),
		queryFn: ({ signal }) => fetchPanelSession({ signal }),
		retry: false,
		staleTime: 5 * 60_000,
	})
}

/**
 * Requests a login code for a provisioned, ACTIVE Expert.
 *
 * Everything else — an unknown number, a deactivated Expert — fails with the same
 * generic `not_authorised` and dispatches no WhatsApp message. The panel shows
 * the server's message unchanged; branching on the reason here would reintroduce
 * the enumeration hole the API is careful to close.
 */
export function useRequestPanelOtp() {
	const codeSent = usePanelEntryStore(state => state.codeSent)
	const recordLockout = usePanelEntryStore(state => state.recordLockout)

	return useMutation({
		mutationFn: ({ mobileNumber }: PanelOtpRequestVariables) => requestPanelOtp(mobileNumber),
		onSuccess: (challenge, variables) => {
			logger.debug("analytics: panel OTP request succeeded; recording event", {
				event: "otp_requested",
				is_resend: variables.isResend,
			})
			track("otp_requested", {
				surface: "expert_panel",
				is_new_number: false,
				is_resend: variables.isResend,
			})
			analytics.flush()
			codeSent(challenge)
		},
		onError: (error: unknown) => {
			if (isOtpLockout(error)) {
				recordLockout({
					message: lockoutMessage(error.retryAfterSeconds),
					retryAfterSeconds: error.retryAfterSeconds,
				})
			}
		},
	})
}

type PanelOtpRequestVariables = {
	mobileNumber: string
	isResend: boolean
}

export type PanelVerifyVariables = {
	mobileNumber: string
	code: string
}

/**
 * Verifies the code and opens the session.
 *
 * The held destination is taken here so a deep link survives re-verification and
 * lands the Expert on the query or vetting request they tapped rather than on the
 * dashboard (US-01-001.22, US-01-004.15, US-01-005.24).
 */
export function useVerifyPanelOtp() {
	const queryClient = useQueryClient()
	const markAuthenticated = useSessionStore(state => state.markAuthenticated)
	const setDestination = useSessionStore(state => state.setDestination)

	return useMutation({
		mutationFn: async ({ mobileNumber, code }: PanelVerifyVariables) => {
			const session = await verifyPanelOtp(mobileNumber, code)
			const destination = await takeDestination()
			return { session, destination }
		},
		onSuccess: ({ session, destination }) => {
			track("login_succeeded", {
				surface: "expert_panel",
				is_new_user: session.isNewAccount ?? false,
			})
			analytics.flush()
			// Invalidated rather than seeded: verification answers a token and an
			// identity, and the session query holds what `GET /expert/me` returns.
			void queryClient.invalidateQueries({ queryKey: panelAuthKeys.session() })
			markAuthenticated()
			setDestination(destination)
			// NOT reset here. Clearing the entry store flips `panelEntryPhase` back
			// to "number", which unmounts <CodeStep> — and React Query skips a
			// mutate()-level onSuccess whose component has gone. That callback in
			// routes/login.tsx is what pushes the held destination or navigates to
			// "/", so a correct code verified, authenticated, and left the Expert
			// sat on the login screen — the same defect the user app hit
			// (TC-US-00-001-1-1 step 4). The login screen clears its own state when
			// it unmounts instead, which also covers leaving the flow any other way.
		},
		onError: (error: unknown) => {
			if (isOtpLockout(error)) {
				usePanelEntryStore.getState().recordLockout({
					message: lockoutMessage(error.retryAfterSeconds),
					retryAfterSeconds: error.retryAfterSeconds,
				})
			}
		},
	})
}

/**
 * Signs out (US-01-005.23).
 *
 * The cookie is cleared and the session invalidated server-side, so the same
 * cookie replayed is rejected. The cache is emptied because the panel holds other
 * people's tax notices.
 */
export function usePanelSignOut() {
	const queryClient = useQueryClient()
	const markSignedOut = useSessionStore(state => state.markSignedOut)

	return useMutation({
		mutationFn: async () => {
			await panelSignOut()
			await endSession()
		},
		onSettled: () => {
			markSignedOut()
			usePanelEntryStore.getState().reset()
			queryClient.clear()
		},
	})
}
